/**
 * Marker Color Configuration
 *
 * Centralized configuration for Google Maps marker colors based on device status.
 * Modify these values to change marker appearance across the application.
 */

export interface MarkerColorConfig {
  /** Color used for online/active devices */
  online: string
  /** Color used for offline/inactive devices */
  offline: string
  /** Color used for pending/unknown status devices */
  pending: string
}

/**
 * Default marker colors using Tailwind CSS color palette
 *
 * Status meanings:
 * - ONLINE: Device has sent data within threshold (default: 360 minutes)
 * - OFFLINE: Device has not sent data within threshold
 * - PENDING: Device status unknown (no timestamp data or loading)
 */
export const MARKER_COLORS: MarkerColorConfig = {
  /** Green-500: Devices actively sending data */
  online: '#22c55e',

  /** Gray-300: Devices that have stopped reporting */
  offline: '#d1d5db',

  /** Gray-300: Devices with unknown/pending status */
  pending: '#d1d5db',
}

/**
 * Alternative color schemes (examples)
 * Uncomment and replace MARKER_COLORS values above to use these:
 */

// High contrast color scheme
export const HIGH_CONTRAST_COLORS: MarkerColorConfig = {
  online: '#00ff00',   // Bright green
  offline: '#ff0000',  // Bright red
  pending: '#808080',  // Medium gray
}

// Colorblind-friendly scheme (protanopia/deuteranopia)
export const COLORBLIND_FRIENDLY_COLORS: MarkerColorConfig = {
  online: '#0088ff',   // Blue (online)
  offline: '#ff8800',  // Orange (offline)
  pending: '#888888',  // Gray (pending)
}

// Dark mode optimized colors
export const DARK_MODE_COLORS: MarkerColorConfig = {
  online: '#34d399',   // Emerald-400
  offline: '#f87171',  // Red-400
  pending: '#6b7280',  // Gray-500
}

/**
 * Get marker color based on device status
 * @param status Device status: 'online', 'offline', or 'pending'
 * @returns Hex color code for map marker
 */
export function getMarkerColor(status: 'online' | 'offline' | 'pending'): string {
  return MARKER_COLORS[status] || MARKER_COLORS.pending
}

/**
 * Tailwind CSS class names for status indicators
 * Used for status badges, text, and backgrounds in the UI
 */
export const STATUS_CLASSES = {
  online: {
    text: 'text-green-500',
    bg: 'bg-green-500',
    ring: 'ring-green-500',
  },
  offline: {
    text: 'text-gray-300',
    bg: 'bg-gray-300',
    ring: 'ring-gray-300',
  },
  pending: {
    text: 'text-gray-300',
    bg: 'bg-gray-300',
    ring: 'ring-gray-300',
  },
} as const

/**
 * Status display labels
 */
export const STATUS_LABELS = {
  online: 'Online',
  offline: 'Offline',
  pending: 'Pending',
} as const
